import React from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Check, CheckCheck, Reply, Forward, Trash2, MoreVertical, Ban, CornerUpRight, SmilePlus, Copy, Pencil, Scissors, TextCursorInput } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import AudioMessage from "@/components/chat/AudioMessage";
import ChatMediaViewer from "@/components/chat/ChatMediaViewer";
import { linkify } from "@/components/ExpandableText";
import LinkPreviewCard from "@/components/LinkPreviewCard";

const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];
const LONG_PRESS_MS = 450;
const COARSE = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)")?.matches;

const time = (iso) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Texto copiado");
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text; document.body.appendChild(ta); ta.select();
    try { document.execCommand("copy"); toast.success("Texto copiado"); } catch { toast.error("Não foi possível copiar"); }
    document.body.removeChild(ta);
  }
}

function Reactions({ m, me, onReact }) {
  const list = m.reactions || [];
  if (!list.length) return null;
  const groups = list.reduce((acc, r) => { (acc[r.emoji] = acc[r.emoji] || []).push(r); return acc; }, {});
  return (
    <div className="flex flex-wrap gap-1 mt-1" data-testid={`reactions-${m.message_id}`}>
      {Object.entries(groups).map(([emoji, rs]) => (
        <button key={emoji} type="button" onClick={() => onReact(m, emoji)} title={rs.map(r => r.name).join(", ")}
          data-testid={`reaction-${m.message_id}-${emoji}`}
          className={`text-xs rounded-full px-1.5 py-0.5 border bg-background text-foreground flex items-center gap-1 ${rs.some(r => r.user_id === me) ? "border-primary" : "border-border"}`}>
          <span>{emoji}</span><span className="font-semibold">{rs.length}</span>
        </button>
      ))}
    </div>
  );
}

function Ticks({ m, othersCount }) {
  if (m.pending) return <span className="text-[10px]">⏱</span>;
  const delivered = (m.delivered_to || []).length > 0;
  const read = (m.read_by || []).length >= Math.max(1, othersCount);
  if (read) return <CheckCheck className="w-3.5 h-3.5 text-sky-300" data-testid={`tick-read-${m.message_id}`} />;
  if (delivered) return <CheckCheck className="w-3.5 h-3.5" data-testid={`tick-delivered-${m.message_id}`} />;
  return <Check className="w-3.5 h-3.5" data-testid={`tick-sent-${m.message_id}`} />;
}

/** Context menu shown after long-press / right-click on a text message. */
function TextActionsMenu({ m, mine, anchor, onClose, onEdit, onCut, onSelect }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("pointerdown", away, true);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("pointerdown", away, true); document.removeEventListener("keydown", esc); };
  }, [onClose]);
  const W = 200, H = mine ? 180 : 100;
  const left = Math.max(8, Math.min(anchor.x - W / 2, window.innerWidth - W - 8));
  const top = anchor.y + H + 12 > window.innerHeight ? Math.max(8, anchor.y - H - 12) : anchor.y + 8;
  const Item = ({ icon: Icon, label, onClick, testid, danger }) => (
    <button type="button" data-testid={testid} onClick={() => { onClick(); onClose(); }}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted ${danger ? "text-red-600" : ""}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
  return createPortal(
    <div ref={ref} data-testid={`text-actions-${m.message_id}`} role="menu"
      className="fixed z-[90] w-[200px] rounded-xl border bg-popover text-popover-foreground shadow-xl py-1 animate-in fade-in zoom-in-95"
      style={{ left, top }}>
      <Item icon={Copy} label="Copiar" testid={`text-copy-${m.message_id}`} onClick={() => copyText(m.content)} />
      <Item icon={TextCursorInput} label="Selecionar texto" testid={`text-select-${m.message_id}`} onClick={onSelect} />
      {mine && <Item icon={Pencil} label="Editar" testid={`text-edit-${m.message_id}`} onClick={() => onEdit(m)} />}
      <Item icon={Scissors} label="Recortar" testid={`text-cut-${m.message_id}`} onClick={() => onCut(m)} />
    </div>,
    document.body
  );
}

export default function MessageBubble({ m, mine, isGroup, othersCount, onReply, onForward, onDelete, onReact, onEdit, onCut, me }) {
  const deleted = m.deleted_for_all;
  const [whoOpen, setWhoOpen] = React.useState(false);
  const [viewerSrc, setViewerSrc] = React.useState(null);
  const [menuAt, setMenuAt] = React.useState(null);
  const [selecting, setSelecting] = React.useState(false);
  const textRef = React.useRef(null);
  const pressTimer = React.useRef(null);
  const pressStart = React.useRef(null);

  const isText = m.type === "text" && !!m.content && !deleted && !m.pending;

  const openMenu = (x, y) => setMenuAt({ x, y });
  const onTextPointerDown = (e) => {
    if (!isText || e.button === 2) return;
    pressStart.current = { x: e.clientX, y: e.clientY };
    clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => {
      if (navigator.vibrate) navigator.vibrate(10);
      openMenu(e.clientX, e.clientY);
    }, LONG_PRESS_MS);
  };
  const cancelPress = (e) => {
    if (e && pressStart.current && e.type === "pointermove") {
      if (Math.hypot(e.clientX - pressStart.current.x, e.clientY - pressStart.current.y) < 8) return;
    }
    clearTimeout(pressTimer.current);
  };
  const onContextMenu = (e) => {
    if (!isText) return;
    e.preventDefault();
    openMenu(e.clientX, e.clientY);
  };
  const selectText = () => {
    const el = textRef.current;
    if (!el) return;
    setSelecting(true);
    requestAnimationFrame(() => {
      const range = document.createRange();
      range.selectNodeContents(el);
      const sel = window.getSelection();
      sel.removeAllRanges(); sel.addRange(range);
    });
  };

  return (
    <div className={`group flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.message_id}`}>
      {!mine && (
        <Avatar className="w-7 h-7 shrink-0" data-testid={`message-avatar-${m.message_id}`}>
          <AvatarImage src={m.from_picture} />
          <AvatarFallback className="text-[10px]">{m.from_name?.[0]}</AvatarFallback>
        </Avatar>
      )}
      <div className={`relative max-w-[78%] rounded-2xl px-3 py-2 shadow-sm ${mine ? "bg-gradient-to-br from-teal-500 via-cyan-500 to-sky-600 text-white rounded-br-md shadow-cyan-500/25" : "bg-card border border-cyan-500/15 rounded-bl-md"} ${m.pending ? "opacity-70" : ""} ${menuAt ? "ring-2 ring-cyan-300/70" : ""}`}>
        {isGroup && !mine && <div className="text-[11px] font-semibold text-emerald-600 mb-0.5">{m.from_name}</div>}
        {m.forwarded && !deleted && <div className="text-[10px] italic opacity-70 flex items-center gap-1 mb-0.5"><CornerUpRight className="w-3 h-3" /> Encaminhada</div>}
        {m.reply_to && !deleted && (
          <div className={`text-xs rounded-lg px-2 py-1 mb-1 border-l-2 ${mine ? "bg-black/15 border-white/70" : "bg-cyan-500/5 border-cyan-500"}`} data-testid={`reply-preview-${m.message_id}`}>
            <div className="font-semibold truncate">{m.reply_to.from_name}</div>
            <div className="truncate opacity-80">{m.reply_to.preview}</div>
          </div>
        )}
        {m.story_reply && !deleted && (
          <div className={`flex items-stretch gap-2 rounded-lg px-2 py-1.5 mb-1 border-l-2 ${mine ? "bg-black/15 border-white/70" : "bg-cyan-500/5 border-cyan-500"}`} data-testid={`story-reply-quote-${m.message_id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-semibold">
                {m.story_reply.reaction && !(m.content && m.content !== m.story_reply.reaction)
                  ? (mine ? `Você reagiu ao story de ${m.story_reply.author_name || ""}` : "Reagiu ao seu story")
                  : (mine ? `Você respondeu ao story de ${m.story_reply.author_name || ""}` : "Respondeu ao seu story")}
              </div>
              {m.story_reply.text && <div className="text-[11px] opacity-80 truncate">“{m.story_reply.text}”</div>}
            </div>
            <div className="w-10 h-14 rounded-md overflow-hidden shrink-0 flex items-center justify-center text-[8px] text-white text-center" style={{ background: m.story_reply.bg_color || "#111827" }}>
              {m.story_reply.image_url ? <img src={m.story_reply.image_url} alt="" className="w-full h-full object-cover" />
                : m.story_reply.video_url ? <video src={m.story_reply.video_url} muted preload="metadata" className="w-full h-full object-cover" />
                : <span className="px-0.5 line-clamp-3">{m.story_reply.text}</span>}
            </div>
          </div>
        )}
        {deleted ? (
          <div className="text-sm italic opacity-70 flex items-center gap-1" data-testid={`message-deleted-${m.message_id}`}><Ban className="w-3.5 h-3.5" /> Mensagem apagada</div>
        ) : (
          <>
            {m.type === "image" && (
              <button type="button" onClick={() => setViewerSrc(m.media_url)} className="block mb-1 cursor-zoom-in" aria-label="Abrir foto em tela cheia" data-testid={`message-image-open-${m.message_id}`}>
                <img src={m.media_url} alt="" className="rounded-lg max-w-full max-h-72" data-testid={`message-image-${m.message_id}`} />
              </button>
            )}
            {m.type === "video" && <video src={m.media_url} controls playsInline preload="metadata" className="rounded-lg max-w-full max-h-72 mb-1 bg-black" data-testid={`message-video-${m.message_id}`} />}
            {m.type === "audio" && <AudioMessage src={m.media_url} duration={m.duration} mine={mine} testid={`message-audio-${m.message_id}`} />}
            {m.link_preview && !deleted && <LinkPreviewCard preview={m.link_preview} compact dark={mine} testid={`message-link-${m.message_id}`} />}
            {m.content && (
              <div
                ref={textRef}
                data-testid={`message-text-${m.message_id}`}
                className={`text-sm whitespace-pre-wrap break-words ${selecting || !COARSE ? "select-text" : "select-none"}`}
                style={{ WebkitTouchCallout: "none" }}
                onPointerDown={onTextPointerDown}
                onPointerUp={cancelPress}
                onPointerLeave={cancelPress}
                onPointerMove={cancelPress}
                onContextMenu={onContextMenu}
              >
                {linkify(m.content, { dark: mine, mentions: m.mentions })}
              </div>
            )}
          </>
        )}
        <div className={`flex items-center justify-end gap-1 text-[10px] mt-0.5 ${mine ? "text-white/80" : "text-muted-foreground"}`}>
          {m.edited_at && !deleted && <span className="italic opacity-80" data-testid={`message-edited-${m.message_id}`}>editada</span>}
          <span>{time(m.created_at)}</span>
          {mine && <Ticks m={m} othersCount={othersCount} />}
        </div>
        <Reactions m={m} me={me} onReact={onReact} />
        {(m.reactions || []).length > 0 && (
          <button type="button" onClick={() => setWhoOpen(o => !o)} data-testid={`reactions-who-${m.message_id}`} className={`text-[10px] underline ${mine ? "text-white/70" : "text-muted-foreground"}`}>{whoOpen ? "ocultar" : "quem reagiu"}</button>
        )}
        {whoOpen && (
          <ul className={`text-[11px] mt-1 space-y-0.5 ${mine ? "text-white/90" : "text-muted-foreground"}`} data-testid={`reactions-list-${m.message_id}`}>
            {(m.reactions || []).map(r => <li key={r.user_id}>{r.emoji} {r.name}</li>)}
          </ul>
        )}
        {!m.pending && !deleted && (
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid={`message-react-${m.message_id}`} className={`absolute -bottom-2 ${mine ? "-left-2" : "-right-2"} w-6 h-6 rounded-full bg-background border shadow flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity`} aria-label="Reagir">
                <SmilePlus className="w-3.5 h-3.5 text-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-1 flex gap-1" side="top" data-testid={`react-picker-${m.message_id}`}>
              {QUICK_REACTIONS.map(e => <button key={e} type="button" onClick={() => onReact(m, e)} data-testid={`react-${m.message_id}-${e}`} className="text-xl hover:scale-125 transition-transform px-1">{e}</button>)}
            </PopoverContent>
          </Popover>
        )}
        {!m.pending && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`message-menu-${m.message_id}`} className={`absolute -top-2 ${mine ? "-left-2" : "-right-2"} w-6 h-6 rounded-full bg-background border shadow flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity`} aria-label="Opções">
                <MoreVertical className="w-3.5 h-3.5 text-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={mine ? "end" : "start"}>
              {!deleted && <DropdownMenuItem data-testid={`message-reply-${m.message_id}`} onClick={() => onReply(m)}><Reply className="w-4 h-4 mr-2" /> Responder</DropdownMenuItem>}
              {!deleted && <DropdownMenuItem data-testid={`message-forward-${m.message_id}`} onClick={() => onForward(m)}><Forward className="w-4 h-4 mr-2" /> Encaminhar</DropdownMenuItem>}
              {isText && <DropdownMenuItem data-testid={`message-copy-${m.message_id}`} onClick={() => copyText(m.content)}><Copy className="w-4 h-4 mr-2" /> Copiar</DropdownMenuItem>}
              {isText && mine && <DropdownMenuItem data-testid={`message-edit-${m.message_id}`} onClick={() => onEdit(m)}><Pencil className="w-4 h-4 mr-2" /> Editar</DropdownMenuItem>}
              <DropdownMenuItem data-testid={`message-delete-me-${m.message_id}`} onClick={() => onDelete(m, "me")}><Trash2 className="w-4 h-4 mr-2" /> Apagar para mim</DropdownMenuItem>
              {mine && !deleted && <DropdownMenuItem data-testid={`message-delete-all-${m.message_id}`} className="text-red-600" onClick={() => onDelete(m, "all")}><Trash2 className="w-4 h-4 mr-2" /> Apagar para todos</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {menuAt && (
        <TextActionsMenu m={m} mine={mine} anchor={menuAt} onClose={() => setMenuAt(null)} onEdit={onEdit} onCut={onCut} onSelect={selectText} />
      )}
      {viewerSrc && <ChatMediaViewer src={viewerSrc} onClose={() => setViewerSrc(null)} />}
    </div>
  );
}
